/**
 * Convert raw backend / provider error strings into clean, professional,
 * user-facing messages. Never expose internals like "Gemini", "rate limit",
 * "empty response", "429", stack traces, etc.
 *
 * Always log the raw value to console for debugging — only the return value
 * of these helpers should reach the UI.
 *
 * NOTE: For new code prefer `handleError(...)` from `@/shared/lib/errors/handleError`.
 * It calls these helpers internally and also logs structured context + toasts
 * in one step. Use the bare helpers below only when you need the friendly
 * copy without any side effects (e.g. inline form-field rendering).
 */

type FriendlyKind =
  | "rate_limit"
  | "server_busy"
  | "timeout"
  | "auth"
  | "insufficient_mana"
  | "content_blocked"
  | "generic";

const PATTERNS: Array<{ kind: FriendlyKind; test: RegExp }> = [
  { kind: "insufficient_mana", test: /insufficient[_\s-]?mana|not enough mana|402/i },
  { kind: "rate_limit", test: /rate[_\s-]?limit|429|too many requests|quota/i },
  { kind: "server_busy", test: /503|server busy|unavailable|overloaded|capacity|empty response|gemini|openai|anthropic|model/i },
  { kind: "timeout", test: /timeout|timed out|aborted|abort(?:error)?|network|fetch failed|econnreset/i },
  { kind: "auth", test: /401|403|unauthor|forbidden|jwt|token/i },
  { kind: "content_blocked", test: /safety|blocked|moderation|policy|prohibited|inappropriate/i },
];

const FRIENDLY: Record<FriendlyKind, string> = {
  rate_limit: "We're processing a lot of requests right now. Please wait a moment and try again.",
  server_busy: "Our servers are experiencing high demand. Please try again in a few minutes.",
  timeout: "The connection was interrupted. Please try again.",
  auth: "Your session has expired. Please sign in again.",
  insufficient_mana: "You don't have enough Mana to continue.",
  content_blocked: "Rejected by the safety filter. RecapManga doesn't process adult or sexually explicit material, or content that promotes hate, harassment or violence.",
  generic: "Something went wrong. Please try again.",
};

const FRIENDLY_TITLE: Record<FriendlyKind, string> = {
  rate_limit: "Please wait a moment",
  server_busy: "High demand",
  timeout: "Connection interrupted",
  auth: "Session expired",
  insufficient_mana: "Insufficient Mana",
  content_blocked: "Content not allowed",
  generic: "Processing failed",
};

function classify(raw: unknown): FriendlyKind {
  const text = (() => {
    if (!raw) return "";
    if (typeof raw === "string") return raw;
    if (raw instanceof Error) return raw.message;
    if (typeof raw === "object") {
      const r = raw as Record<string, unknown>;
      return String(r.message ?? r.error ?? "");
    }
    return String(raw);
  })();
  for (const { kind, test } of PATTERNS) {
    if (test.test(text)) return kind;
  }
  return "generic";
}

/**
 * Get a clean, user-facing description for any error.
 * Always log the raw error separately via console.error.
 */
export function getFriendlyErrorMessage(raw: unknown, fallback?: string): string {
  const kind = classify(raw);
  if (kind === "generic" && fallback) return fallback;
  return FRIENDLY[kind];
}

export function getFriendlyErrorTitle(raw: unknown, fallback = "Processing failed"): string {
  const kind = classify(raw);
  if (kind === "generic") return fallback;
  return FRIENDLY_TITLE[kind];
}

/**
 * Convenience helper — returns both for toast() calls.
 */
export function getFriendlyError(raw: unknown, fallback?: string): {
  title: string;
  description: string;
} {
  return {
    title: getFriendlyErrorTitle(raw),
    description: getFriendlyErrorMessage(raw, fallback),
  };
}
